/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import './style/withBackground.scss';

import React, {CSSProperties, PropsWithChildren, useEffect, useMemo, useRef} from 'react';
import classNames from 'classnames';
import {escapePressed, hasHover} from "../util/controlUtil";

interface Props {
    onClickBackground?: Function,
    onClose: Function;
    active: boolean;
    backgroundColor?: string;
    transparent?: boolean;
    backgroundClass?: string;
    containerClass?: string;
    escapeDisabled?: boolean;
    preventBackgroundClick?: boolean;
    zIndex?: number;
    allowBodyScroll?: boolean;
}

export default function WithBackground(props: PropsWithChildren<Props>) {
    const {
        active, escapeDisabled, backgroundClass, containerClass, children, backgroundColor, preventBackgroundClick,
        onClickBackground, onClose, transparent, zIndex, allowBodyScroll
    } = props;
    const escapeListenerActive = useRef(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const backgroundStyle = useMemo(() => {
        const color = transparent ? 'transparent' : backgroundColor ?? 'rgba(0,0,0,.2)';
        const style: CSSProperties = {backgroundColor: color};
        if (zIndex !== undefined) style.zIndex = zIndex;
        return style;
    }, [transparent, backgroundColor, zIndex]);

    function _onClickBackground(evt: React.MouseEvent) {
        if (preventBackgroundClick) return;
        if (onClickBackground) onClickBackground(evt);
        else if (!hasHover(contentRef.current)) onClose(evt);
    }

    function onKeyDown(evt: KeyboardEvent) {
        if (escapeDisabled) return;
        if (escapePressed(evt)) onClose();
    }

    function addEscapeListener() {
        if (escapeListenerActive.current) return;
        document.addEventListener('keydown', onKeyDown);
        escapeListenerActive.current = true;
    }

    function removeEscapeListener() {
        document.removeEventListener('keydown', onKeyDown);
        escapeListenerActive.current = false;
    }

    function setBodyScroll(active: boolean) {
        if (allowBodyScroll) return;
        if (active)
            document.body.style.overflowY = 'hidden';
        else document.body.style.removeProperty('overflow-y');
    }

    useEffect(() => {
        if (escapeDisabled) removeEscapeListener();
        else if (active) addEscapeListener();
        return function () {
            removeEscapeListener();
        }
    }, [active, escapeDisabled]);

    useEffect(() => {
        setBodyScroll(active);
        return function () {
            removeEscapeListener();
        }
    }, [active]);

    if (!active) return null;
    return (
        <div className={classNames('with-background', backgroundClass)} onMouseDown={_onClickBackground}
             style={backgroundStyle}>
            <div className={classNames('content-container', containerClass)} ref={contentRef}>
                {children}
            </div>
        </div>
    )
}